import type { WasteSenseEventRow, WasteSenseSite } from "./types";

export type Weekday = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun";

const WEEKDAYS: readonly Weekday[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export interface DailyActivitySeries {
  /** Weekday labels in display order (Mon → Sun). */
  labels: readonly Weekday[];
  /** People counts per weekday, same length & order as `labels`. */
  people: readonly number[];
  /** Vehicle counts per weekday, same length & order as `labels`. */
  vehicles: readonly number[];
  /** Maximum across both series; used for chart scale. Always >= 1. */
  vmax: number;
}

/**
 * Bucket a site's event rows into per-weekday counts of people-vs-vehicle activity.
 *
 * Activity classification mirrors the Python reporting pipeline: an event
 * is counted as a "vehicle" event when its `activity_type` mentions a
 * vehicle, otherwise as a "people" event. Events with neither tag still
 * count as people activity — matches the upstream fallback.
 *
 * Buckets are computed in the site's IANA timezone so the chart agrees
 * with the events table — both render an event under the same weekday
 * regardless of where the report is being read. Without this, an event
 * at 22:30 UTC in Sydney shows as Tuesday 09:30 in the table but
 * (incorrectly) lands on the Monday bar in the chart.
 *
 * If the site has no rows at all we return zero-filled series so the
 * chart renders an empty baseline rather than throwing.
 */
export function computeDailyActivity(site: WasteSenseSite): DailyActivitySeries {
  const people: number[] = [0, 0, 0, 0, 0, 0, 0];
  const vehicles: number[] = [0, 0, 0, 0, 0, 0, 0];
  const bucketAt = makeWeekdayBucketer(site.timezone);

  for (const row of site.rows ?? []) {
    const idx = bucketAt(row.timestamp);
    if (idx === null) continue;
    if (looksLikeVehicleActivity(row)) {
      vehicles[idx] = (vehicles[idx] ?? 0) + 1;
    } else {
      people[idx] = (people[idx] ?? 0) + 1;
    }
  }

  const vmax = Math.max(1, ...people, ...vehicles);
  return { labels: WEEKDAYS, people, vehicles, vmax };
}

/**
 * Returns a function that maps an ISO timestamp to a Mon-based weekday
 * index (0 = Mon … 6 = Sun) in the supplied IANA timezone, or `null`
 * for unparseable timestamps.
 *
 * `Date#getDay()` only knows the viewer's local zone, so we use
 * `Intl.DateTimeFormat` with a fixed `en-US` locale and `weekday: "short"`
 * to extract the weekday in an arbitrary zone. We pin the locale rather
 * than using `undefined` so the abbreviation is deterministic ("Mon",
 * "Tue", …) regardless of the runtime locale — otherwise a French
 * browser would emit "lun." and miss every bucket.
 *
 * The formatter is built once per call site (cheap) and reused across
 * every row for that site.
 */
function makeWeekdayBucketer(timeZone: string): (iso: string) => number | null {
  const fmt = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone });
  return (iso) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return WEEKDAY_INDEX[fmt.format(date)] ?? null;
  };
}

const WEEKDAY_INDEX: Readonly<Record<string, number>> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

function looksLikeVehicleActivity(row: WasteSenseEventRow): boolean {
  const haystack = `${row.activity_type} ${row.interesting_tags.join(" ")}`.toLowerCase();
  return /vehicle|car|truck|van/.test(haystack);
}
